import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import {
  BookOpen,
  Headphones,
  FileText,
  Mic,
  CheckCircle,
  ChevronDown,
  ClipboardCheck,
  Users,
  Laptop,
  Clock,
  Calendar,
} from "lucide-react";
import ContactForm from "../components/ContactForm";

const CELPIPPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const contactRef = useRef(null);

  const faqs = [
    {
      q: "What is the CELPIP test used for?",
      a: "CELPIP is widely accepted for Canadian permanent residency, citizenship, and some professional designations.",
    },
    {
      q: "How long does the CELPIP test take?",
      a: "The CELPIP-General test takes about 3 hours to complete in one sitting, with no separate speaking session.",
    },
    {
      q: "Which is easier, CELPIP or IELTS?",
      a: "CELPIP is fully computer-delivered and uses Canadian accents, while IELTS may involve face-to-face speaking. The easier test depends on your comfort level.",
    },
    {
      q: "How is CELPIP scored?",
      a: "CELPIP is scored on a scale of 1 to 12 for each component, aligned with Canadian Language Benchmarks (CLB).",
    },
    {
      q: "Where can I take the CELPIP test?",
      a: "CELPIP test centers are available across Canada and in select international cities worldwide.",
    },
  ];

  const testFormat = [
    {
      icon: Headphones,
      title: "Listening",
      duration: "47–55 minutes",
      description:
        "6 sections testing comprehension of conversations, news items, and problem-solving.",
    },
    {
      icon: BookOpen,
      title: "Reading",
      duration: "55–60 minutes",
      description:
        "Includes reading correspondence, information, and viewpoints with multiple-choice questions.",
    },
    {
      icon: FileText,
      title: "Writing",
      duration: "53–60 minutes",
      description:
        "Two tasks: writing an email and responding to a survey question with arguments.",
    },
    {
      icon: Mic,
      title: "Speaking",
      duration: "15–20 minutes",
      description:
        "Eight recorded tasks, such as giving advice, describing, and expressing opinions.",
    },
  ];

  const advantages = [
    "100% computer-delivered test with no paper or face-to-face speaking.",
    "Single sitting of about 3 hours covering all skills.",
    "Scores aligned with Canadian Language Benchmarks (CLB).",
    "Results delivered in 4–5 business days.",
    "Uses Canadian English — ideal for immigration to Canada.",
  ];

  const testTypes = [
    {
      title: "CELPIP – General Test",
      desc: "Assesses listening, reading, writing, and speaking skills. Accepted for permanent residency, immigration, and professional purposes.",
    },
    {
      title: "CELPIP – General LS Test",
      desc: "Assesses only listening and speaking skills. Accepted for Canadian citizenship applications and certain professional licenses.",
    },
  ];

  const whyUs = [
    {
      icon: ClipboardCheck,
      title: "Expert Trainers",
      desc: "Coaching from certified trainers with deep knowledge of CELPIP structure and scoring.",
    },
    {
      icon: Laptop,
      title: "Computer-Based Practice",
      desc: "Practice tests that simulate the actual CELPIP computer-based format.",
    },
    {
      icon: Users,
      title: "Personalized Guidance",
      desc: "One-on-one mentoring, feedback, and strategies tailored to your weak areas.",
    },
    {
      icon: Clock,
      title: "Flexible Scheduling",
      desc: "Weekend and evening batches available to fit your lifestyle.",
    },
  ];

  const pricingPlans = [
    {
      title: "Basic Plan",
      price: "₹4,999",
      features: [
        "1 Month Course",
        "Access to All Modules",
        "Weekly Practice Tests",
        "Study Materials Included",
      ],
    },
    {
      title: "Standard Plan",
      price: "₹7,999",
      features: [
        "2 Months Course",
        "All Basic Features",
        "Extra Mock Tests",
        "One-on-One Feedback Sessions",
      ],
      popular: true,
    },
    {
      title: "Premium Plan",
      price: "₹11,999",
      features: [
        "3 Months Course",
        "Unlimited Mock Tests",
        "Personal Mentor",
        "Guaranteed Improvement",
      ],
    },
  ];

  const events = [
    {
      date: "Sept 10, 2025",
      title: "CELPIP Orientation Workshop",
      desc: "Free workshop to understand CELPIP modules & scoring system.",
    },
    {
      date: "Sept 20, 2025",
      title: "Speaking Masterclass",
      desc: "Live session with expert trainer focusing on fluency & clarity.",
    },
    {
      date: "Oct 5, 2025",
      title: "Full-Length Mock Test",
      desc: "Simulated CELPIP test under exam conditions.",
    },
  ];

  return (
    <div className="celpip-page font-sans bg-gray-50 text-gray-800">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 text-white py-24 px-6 text-center overflow-hidden">
        <motion.h1
          initial={{ opacity: 0, y: -40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9 }}
          className="text-4xl md:text-5xl font-bold mb-6 drop-shadow-lg"
        >
          CELPIP – Canadian English Language Proficiency Index Program
        </motion.h1>
        <p className="max-w-3xl mx-auto text-lg opacity-90 leading-relaxed">
          The CELPIP test is your pathway to Canadian permanent residency,
          citizenship, and professional recognition. Recognized by IRCC and
          professional bodies across Canada.
        </p>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-8 flex justify-center"
        >
          <button
            onClick={() =>
              contactRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            className="px-7 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-full shadow-lg hover:opacity-90 transition"
          >
            Book a Free Consultation
          </button>
        </motion.div>
      </section>

      {/* Types of CELPIP */}
      <section className="py-20 px-6 bg-white">
        <h2 className="text-3xl font-bold text-center mb-14">
          Types of CELPIP Tests
        </h2>
        <div className="grid gap-8 md:grid-cols-2 max-w-5xl mx-auto">
          {testTypes.map((type, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -6 }}
              className="bg-gradient-to-br from-blue-50 to-white shadow-md rounded-2xl p-7 border border-gray-100"
            >
              <h3 className="font-semibold text-xl text-blue-800 mb-3">
                {type.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">{type.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Test Format */}
      <section className="py-20 px-6 bg-gradient-to-b from-gray-100 to-gray-50">
        <h2 className="text-3xl font-bold text-center mb-14">
          CELPIP Test Format
        </h2>
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          {testFormat.map((item, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.05 }}
              className="bg-white shadow-lg rounded-2xl p-6 text-center border border-gray-100"
            >
              <item.icon className="mx-auto text-blue-700 w-12 h-12 mb-4" />
              <h3 className="font-semibold text-xl">{item.title}</h3>
              <p className="text-sm text-gray-500">{item.duration}</p>
              <p className="mt-3 text-sm text-gray-700">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 px-6 bg-white">
        <h2 className="text-3xl font-bold text-center mb-14">
          Why Choose CELPIP?
        </h2>
        <ul className="max-w-4xl mx-auto grid md:grid-cols-2 gap-6">
          {advantages.map((point, i) => (
            <motion.li
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.15 }}
              className="flex items-start bg-gradient-to-br from-white to-blue-50 shadow-md p-5 rounded-xl"
            >
              <CheckCircle className="text-green-600 w-6 h-6 mt-1 flex-shrink-0" />
              <span className="ml-3 text-gray-700">{point}</span>
            </motion.li>
          ))}
        </ul>
      </section>

      {/* Why Us */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-50 to-white">
        <h2 className="text-3xl font-bold text-center mb-14">
          Why Prepare With Us?
        </h2>
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4 max-w-6xl mx-auto">
          {whyUs.map((item, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.05 }}
              className="bg-white p-7 rounded-2xl shadow-lg border border-gray-100 text-center"
            >
              <item.icon className="mx-auto w-12 h-12 text-blue-700 mb-4" />
              <h3 className="font-semibold text-xl mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Classes / Events Section */}
      <section className="bg-gray-100 py-20 px-6">
        <h2 className="text-3xl font-bold text-center mb-14">
          Upcoming Classes & Events
        </h2>
        <div className="max-w-4xl mx-auto space-y-6">
          {events.map((event, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.02 }}
              className="bg-white shadow-md rounded-lg p-6 flex flex-col md:flex-row items-start md:items-center gap-4"
            >
              <Calendar className="w-10 h-10 text-blue-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-xl">{event.title}</h3>
                <p className="text-sm text-gray-500">{event.date}</p>
                <p className="text-gray-600 mt-2">{event.desc}</p>
              </div>
            </motion.div>
            
          ))}
        </div>
        <div className="flex justify-center pt-4">
          <button
            onClick={() =>
              contactRef.current?.scrollIntoView({ behavior: "smooth" })
            }
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-500"
          >
            Register now!
          </button>
        </div>
        
      </section>

      {/* Scoring System */}
      <section className="py-20 px-6 max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-14">
          Scoring System
        </h2>
        <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100">
          <p className="text-gray-700 text-lg mb-6">
            CELPIP scores are reported on a scale from 1 to 12, each mapped to
            the Canadian Language Benchmarks (CLB). Most immigration programs
            require CLB 7 or higher.
          </p>
          <div className="grid md:grid-cols-2 gap-4 text-gray-700">
            <div>
              <ul className="space-y-2">
                <li>CLB 12 = Advanced proficiency</li>
                <li>CLB 9–11 = High proficiency</li>
                <li>CLB 7–8 = Adequate proficiency</li>
              </ul>
            </div>
            <div>
              <ul className="space-y-2">
                <li>CLB 5–6 = Developing proficiency</li>
                <li>CLB 1–4 = Limited proficiency</li>
                <li>CLB 0 = Minimal proficiency</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Preparation Tips */}
      <section className="py-20 px-6 bg-gray-100">
        <h2 className="text-3xl font-bold text-center mb-14">
          Preparation Tips
        </h2>
        <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          <motion.div
            whileHover={{ scale: 1.03 }}
            className="bg-white p-7 rounded-2xl shadow-md"
          >
            <h3 className="font-semibold text-xl mb-4 text-blue-800">
              Listening & Reading
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Practice with CELPIP sample tests. Focus on note-taking, scanning
              for keywords, and managing time effectively.
            </p>
          </motion.div>
          <motion.div
            whileHover={{ scale: 1.03 }}
            className="bg-white p-7 rounded-2xl shadow-md"
          >
            <h3 className="font-semibold text-xl mb-4 text-blue-800">
              Writing & Speaking
            </h3>
            <p className="text-gray-600 leading-relaxed">
              Use structured templates for emails and opinion tasks. Record your
              voice for practice to improve fluency, pronunciation, and
              coherence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 px-6 max-w-5xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-14">
          Frequently Asked Questions
        </h2>
        <div className="space-y-5">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              whileHover={{ scale: 1.02 }}
              className="bg-white shadow-md rounded-xl p-5 cursor-pointer border border-gray-100 transition"
              onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
            >
              <div className="flex justify-between items-center">
                <h4 className="font-semibold text-lg">{faq.q}</h4>
                <ChevronDown
                  className={`w-5 h-5 transform transition-transform ${
                    openFAQ === index ? "rotate-180" : ""
                  }`}
                />
              </div>
              {openFAQ === index && (
                <p className="mt-3 text-gray-600 leading-relaxed">{faq.a}</p>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact / Lead Form */}
      <section ref={contactRef}>
        <div>
          <ContactForm />
        </div>
      </section>
    </div>
  );
};

export default CELPIPPage;
